import { Check } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

import { cn } from '../lib/ui'

/**
 * A small anchored panel: the filter menus, the label picker, the assignee
 * picker. Closes on Escape, on an outside pointer-down and on a scroll away —
 * no portal, so it inherits the stacking context of whatever opened it.
 */
export function Popover({
  trigger,
  children,
  align = 'left',
  width = 'w-64',
  label,
}: {
  trigger: (props: { open: boolean; toggle: () => void; id: string }) => ReactNode
  children: (props: { close: () => void }) => ReactNode
  align?: 'left' | 'right'
  width?: string
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const id = useId()

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    // Capture phase, so an open popover swallows Escape before the dialog or
    // panel behind it sees it — otherwise dismissing a filter menu would also
    // close the issue you opened it from.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open])

  return (
    <div className="relative" ref={containerRef}>
      {trigger({ open, toggle: () => setOpen((value) => !value), id })}
      {open && (
        <div
          id={id}
          role="dialog"
          aria-label={label}
          className={cn(
            'absolute z-30 mt-1 max-h-80 overflow-y-auto overscroll-contain rounded-card border border-ink-200 bg-white p-1 shadow-lg shadow-ink-900/5',
            'dark:border-ink-800 dark:bg-ink-900 dark:shadow-black/40',
            width,
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {children({ close: () => setOpen(false) })}
        </div>
      )}
    </div>
  )
}

export function PopoverItem({
  children,
  onClick,
  selected,
  disabled,
}: {
  children: ReactNode
  onClick: () => void
  selected?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={Boolean(selected)}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition',
        disabled
          ? 'cursor-not-allowed text-ink-400'
          : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800',
      )}
    >
      <span
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded border',
          selected
            ? 'border-brand-600 bg-brand-600 text-white'
            : 'border-ink-300 dark:border-ink-600',
        )}
        aria-hidden="true"
      >
        {selected && <Check className="size-3" strokeWidth={3} aria-hidden="true" />}
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  )
}
